import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Heart, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import Navigation from '@/components/site/Navigation';
import Footer from '@/components/site/Footer';

const AREAS = [
  'Event Logistics', 'Guest Hospitality', 'Speaker Support', 'Sponsor & Exhibitor Support',
  'Media & Communications', 'Youth & Student Engagement', 'Setup & Breakdown', 'General Support',
];
const ROLES = [
  'Registration and Check-In', 'Speaker Support', 'Exhibitor and Sponsor Support',
  'Ballroom and Session Support', 'Hospitality', 'VIP and Reception Support',
  'Media and Red Carpet Support', 'Setup and Breakdown', 'General Event Support',
];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const Section = ({ title, children }) => (
  <Card className="p-6 md:p-8 rounded-2xl border border-aawta-border shadow-aawta-soft space-y-4">
    <h3 className="font-display font-semibold text-xl text-aawta-navy">{title}</h3>
    <div className="space-y-4">{children}</div>
  </Card>
);

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[13.5px] font-medium text-aawta-navy">
      {label} {required && <span className="text-aawta-magenta">*</span>}
    </Label>
    {children}
  </div>
);

const Volunteer = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ areas_of_interest: [], consent_communications: false });
  const [headshot, setHeadshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === 'string' || typeof e === 'boolean' ? e : e.target.value }));
  const toggleArea = (a) => setForm((f) => ({ ...f, areas_of_interest: f.areas_of_interest.includes(a) ? f.areas_of_interest.filter((x) => x !== a) : [...f.areas_of_interest, a] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error('Name and email are required.');
    if (!headshot) return toast.error('Please upload a professional headshot.');
    setSubmitting(true);
    try {
      let headshotId = null;
      if (headshot) {
        const fd = new FormData();
        fd.append('file', headshot);
        fd.append('folder', 'headshots');
        const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        headshotId = data.id;
      }
      await api.post('/volunteer', { ...form, headshot_file_id: headshotId });
      setDone(true); window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <main className="pt-32 pb-24">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center" data-testid="volunteer-thank-you">
            <div className="w-16 h-16 rounded-full bg-aawta-magenta/10 text-aawta-magenta mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-6 text-aawta-navy">Thank You!</h1>
            <div className="divider-magenta mt-6 mx-auto" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Your volunteer application has been received. The AAWTA team will review your
              submission and follow up soon with next steps.
            </p>
            <Button onClick={() => navigate('/')} className="mt-8 rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white px-7 py-6">Back to Home</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aawta-surface">
      <Navigation />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-aawta-muted hover:text-aawta-navy text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </button>
          <div className="mt-6 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-aawta-magenta text-white flex items-center justify-center shrink-0"><Heart className="h-5 w-5" fill="currentColor" /></div>
            <div>
              <span className="overline text-aawta-magenta">Volunteer Application</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 text-aawta-navy leading-[1.05]">Apply to Volunteer</h1>
            </div>
          </div>
          <div className="divider-magenta mt-6" />
          <p className="mt-6 text-[16px] leading-relaxed text-aawta-muted">
            Volunteers are the heart of the Future Workforce Summit. Complete the form below and
            our team will reach out with roles, shifts, and orientation details.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <Section title="Personal Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name" required><Input required data-testid="vol-name" value={form.full_name || ''} onChange={set('full_name')} className="rounded-lg" /></Field>
                <Field label="Email" required><Input type="email" required data-testid="vol-email" value={form.email || ''} onChange={set('email')} className="rounded-lg" /></Field>
                <Field label="Phone"><Input data-testid="vol-phone" value={form.phone || ''} onChange={set('phone')} className="rounded-lg" /></Field>
                <Field label="City & State"><Input data-testid="vol-city" value={form.city_state || ''} onChange={set('city_state')} placeholder="Atlanta, GA" className="rounded-lg" /></Field>
                <Field label="Organization or Company"><Input data-testid="vol-org" value={form.organization || ''} onChange={set('organization')} className="rounded-lg" /></Field>
                <Field label="T-shirt size">
                  <select data-testid="vol-tshirt" value={form.tshirt_size || ''} onChange={set('tshirt_size')} className="w-full h-10 rounded-lg border border-aawta-border bg-white px-3 text-sm">
                    <option value="">Select size…</option>
                    {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Professional headshot" required>
                <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-aawta-border bg-white px-4 py-3 hover:border-aawta-magenta/50" data-testid="vol-headshot-input">
                  <Upload className="h-5 w-5 text-aawta-magenta shrink-0" />
                  <span className="text-[14px] text-aawta-navy font-medium truncate">
                    {headshot?.name || 'Upload headshot (JPG, JPEG or PNG)'}
                  </span>
                  <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={(e) => setHeadshot(e.target.files?.[0] || null)} />
                </label>
                <div className="text-[12px] text-aawta-muted mt-1">Used for the public “Meet Our 2026 Summit Volunteer Team” section once approved.</div>
              </Field>
            </Section>

            <Section title="Volunteer Preferences">
              <Field label="Preferred volunteer role">
                <select data-testid="vol-role" value={form.preferred_role || ''} onChange={set('preferred_role')} className="w-full h-10 rounded-lg border border-aawta-border bg-white px-3 text-sm">
                  <option value="">Select a role…</option>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Areas of interest">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {AREAS.map((a) => (
                    <label key={a} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-[13.5px] ${form.areas_of_interest.includes(a) ? 'bg-aawta-magenta/10 border-aawta-magenta text-aawta-navy' : 'bg-white border-aawta-border'}`}>
                      <Checkbox checked={form.areas_of_interest.includes(a)} onCheckedChange={() => toggleArea(a)} className="border-aawta-navy data-[state=checked]:bg-aawta-magenta data-[state=checked]:border-aawta-magenta" />
                      {a}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Volunteer experience"><Textarea data-testid="vol-exp" rows={3} value={form.volunteer_experience || ''} onChange={set('volunteer_experience')} className="rounded-lg resize-none" /></Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Availability before"><Input data-testid="vol-avail-before" value={form.availability_before || ''} onChange={set('availability_before')} placeholder="Sept 15–16" className="rounded-lg" /></Field>
                <Field label="Availability during"><Input data-testid="vol-avail-during" value={form.availability_during || ''} onChange={set('availability_during')} placeholder="Sept 17 all day" className="rounded-lg" /></Field>
                <Field label="Availability after"><Input data-testid="vol-avail-after" value={form.availability_after || ''} onChange={set('availability_after')} placeholder="Sept 18" className="rounded-lg" /></Field>
              </div>
            </Section>

            <Section title="Additional Information">
              <Field label="Emergency contact"><Input data-testid="vol-emergency" value={form.emergency_contact || ''} onChange={set('emergency_contact')} placeholder="Name · relationship · phone" className="rounded-lg" /></Field>
              <Field label="Accessibility or accommodation requests"><Textarea data-testid="vol-access" rows={2} value={form.accessibility_requirements || ''} onChange={set('accessibility_requirements')} className="rounded-lg resize-none" /></Field>
              <Field label="Additional notes"><Textarea data-testid="vol-notes" rows={2} value={form.additional_notes || ''} onChange={set('additional_notes')} className="rounded-lg resize-none" /></Field>
              <label className="flex items-start gap-3">
                <Checkbox checked={!!form.consent_communications} onCheckedChange={(v) => setForm((f) => ({ ...f, consent_communications: !!v }))} data-testid="vol-consent" className="mt-0.5 border-aawta-navy data-[state=checked]:bg-aawta-magenta data-[state=checked]:border-aawta-magenta" />
                <span className="text-[13.5px] text-aawta-muted leading-snug">I agree to receive Summit volunteer communications from AAWTA.</span>
              </label>
            </Section>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={submitting} data-testid="vol-submit" className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white px-8 py-6 font-medium shadow-aawta-magenta">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Volunteer Application'}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Volunteer;
